// Clue Cards: guess the word. Flip clues two at a time; fewer clues is better. From the notes.

import { h, clear } from '@kit/dom';
import { shuffle } from '@kit/rng';
import type { GameModule, GameContext } from '@shell/types';
import { makeKeyboard, editDistance } from '@games/common/keyboard';
import { CLUE_WORDS, tierFor } from '@content/words/clues';

const WORDS_PER_ROUND = 5;

function mount(host: HTMLElement, ctx: GameContext) {
  const level = ctx.level;
  const tier = tierFor(level);
  const pool = CLUE_WORDS.filter(w => w.tier === tier || (level % 3 === 0 && w.tier === Math.min(3, tier + 1)));
  const words = shuffle(ctx.rng, pool).slice(0, WORDS_PER_ROUND);
  let idx = 0;
  let flipped = 0;
  let guess = '';
  let wrongGuesses = 0;
  let attempts = 0, correct = 0, cluesUsed = 0;
  const t0 = performance.now();
  let tWord = performance.now();
  let locked = false;

  const head = h('div.row', { style: { justifyContent: 'space-between' } }, h('span.muted', null, 'Word ', h('strong', null, '1'), ` of ${words.length}`), h('span.muted.small', null, ''));
  const cards = h('div.grid2', { style: { gap: '8px' } });
  const guessRow = h('div.prompt', { style: { minHeight: '44px', letterSpacing: '2px' } });
  const kb = makeKeyboard({ onKey: k => { if (!locked && guess.length < 16) { guess += k; renderGuess(); } }, onBackspace: () => { guess = guess.slice(0, -1); renderGuess(); }, onEnter: submit, enterLabel: 'Guess' });
  const flipBtn = h('button.btn.primary', { onclick: flip }, 'Flip 2 clues') as HTMLButtonElement;
  const giveBtn = h('button.btn.ghost', { onclick: giveUp }, 'Tell me');
  const actions = h('div.grid2', null, flipBtn, giveBtn);
  host.append(head, cards, guessRow, kb.el, actions);

  function cur() { return words[idx]; }

  function renderCards() {
    clear(cards);
    cur().clues.forEach((c, i) => {
      const on = i < flipped;
      cards.append(h('div.card' + (on ? '.pop' : ''), { style: { minHeight: '64px', display: 'flex', alignItems: 'center', justifyContent: 'center', textAlign: 'center', background: on ? 'var(--surface)' : 'var(--words)', color: on ? '' : '#fff', fontWeight: on ? '700' : '800', fontSize: on ? '15px' : '22px' } }, on ? c : String(i + 1)));
    });
    (head.querySelector('strong') as HTMLElement).textContent = String(idx + 1);
    (head.lastElementChild as HTMLElement).textContent = `Hint: it is about ${cur().category}`;
    flipBtn.disabled = flipped >= cur().clues.length;
    flipBtn.textContent = flipped >= cur().clues.length ? 'All clues shown' : flipped === 0 ? 'Flip 2 clues' : 'Flip 2 more';
    giveBtn.style.visibility = flipped >= cur().clues.length ? 'visible' : 'hidden';
  }

  function renderGuess() { guessRow.textContent = guess ? guess.toUpperCase() : '· · ·'; }

  function flip() {
    if (flipped >= cur().clues.length) return;
    flipped = Math.min(cur().clues.length, flipped + 2);
    ctx.sound('pop');
    renderCards();
  }

  function submit() {
    if (locked) return;
    const g = guess.trim().toLowerCase();
    if (!g) { ctx.say('Type your guess first, or flip a clue.'); return; }
    if (flipped === 0) { ctx.say('Flip at least one pair of clues first.'); return; }
    const w = cur().word;
    const ok = g === w || (w.length >= 6 && editDistance(g, w) <= 1);
    const ms = performance.now() - tWord;
    if (ok) {
      locked = true;
      attempts++; correct++; cluesUsed += flipped;
      const hinted = flipped >= 5;
      ctx.report({ skill: ctx.skill, level, correct: true, hintUsed: hinted, ms, problemId: `clue:${w}` });
      ctx.sound('ding');
      if (flipped <= 2) ctx.charm('charm.clue2');
      ctx.say(g === w ? `Yes! It is ${w}.` : `Close enough! It is spelled ${w}.`);
      setTimeout(next, 1100);
    } else {
      wrongGuesses++;
      ctx.sound('buzz');
      guessRow.classList.remove('shake'); void guessRow.offsetWidth; guessRow.classList.add('shake');
      if (flipped < cur().clues.length) ctx.say(`Not ${g}. Flip more clues and try again.`);
      else if (wrongGuesses >= 3) { giveUp(); return; }
      else ctx.say(`Not ${g}. ${w.length} letters. Starts with ${w[0].toUpperCase()}.`);
      guess = ''; renderGuess();
    }
  }

  function giveUp() {
    if (locked) return;
    locked = true;
    const w = cur().word;
    attempts++; cluesUsed += flipped;
    ctx.report({ skill: ctx.skill, level, correct: false, hintUsed: true, ms: performance.now() - tWord, problemId: `clue:${w}` });
    ctx.say(`It was ${w}. ${cur().clues[4]}`);
    ctx.speak(`It was ${w}.`);
    setTimeout(next, 1600);
  }

  function next() {
    idx++;
    if (idx >= words.length) {
      ctx.end({ attempts, correct, perfect: attempts === correct, durationMs: performance.now() - t0, notes: [`Clues used: ${cluesUsed} of ${words.length * 6}`] });
      return;
    }
    flipped = 0; guess = ''; wrongGuesses = 0; locked = false; tWord = performance.now();
    ctx.say('');
    renderCards(); renderGuess();
  }

  renderCards(); renderGuess();
  return { destroy() { clear(host); } };
}

const mod: GameModule = {
  id: 'words.clue-cards',
  title: 'Clue Cards',
  blurb: 'Flip clues two at a time and guess the word. Fewer clues, more glory.',
  category: 'words',
  skills: ['vocabulary', 'comprehension'],
  levelRange: [1, 10],
  unlock: { stars: 3 },
  mount,
};
export default mod;
